import helpVideos from "./data/help-videos.json"

/**
 * Help / tutorials page.
 *
 * Mirrors the in-app help screen (/help in the web app): a paged grid of "How to use" videos with
 * a playlist call to action. The app reads its list from study-materials/search, which is behind a
 * login - this site has no auth, so the same list lives in data/help-videos.json and the
 * thumbnails come straight from YouTube. Adding a tutorial means adding one entry to that file.
 */

// Six per page, the page size the in-app screen uses.
const PAGE_SIZE = 6;

let currentPage = 1;
let totalPages = 1;
// The element that opened the lightbox, so focus goes back where it came from on close.
let lastFocusedCard = null;

document.addEventListener('DOMContentLoaded', () => {
    const grid = document.getElementById('helpVideos');
    if (!grid) {
        return;
    }

    const videos = Array.isArray(helpVideos) ? helpVideos : [];
    totalPages = Math.max(1, Math.ceil(videos.length / PAGE_SIZE));

    if (!videos.length) {
        toggle(document.getElementById('helpEmptyState'), true);
        return;
    }

    renderPage(videos, 1);
    setupPagination(videos);
    setupLightbox();
});

/** The slice of videos on screen, plus the pagination state that goes with it. */
function renderPage(videos, page) {
    const grid = document.getElementById('helpVideos');
    currentPage = Math.min(Math.max(page, 1), totalPages);

    const start = (currentPage - 1) * PAGE_SIZE;
    const pageVideos = videos.slice(start, start + PAGE_SIZE);

    grid.innerHTML = pageVideos.map(cardMarkup).join('');
    grid.querySelectorAll('.help-video-card').forEach((card) => {
        card.addEventListener('click', () => openLightbox(card));
    });

    renderPagination();
}

function cardMarkup(video) {
    const tags = (video.tags || [])
        .map((tag) => `<span class="help-tag">${escapeHtml(tag)}</span>`)
        .join('');

    return `
        <button type="button" class="help-video-card"
                data-video-id="${escapeHtml(video.id)}"
                data-video-title="${escapeHtml(video.title)}">
            <span class="help-thumbnail-wrapper">
                <img class="help-thumbnail"
                     src="https://i.ytimg.com/vi/${encodeURIComponent(video.id)}/hqdefault.jpg"
                     alt="${escapeHtml(video.title)}"
                     loading="lazy">
                <span class="help-play-badge" aria-hidden="true">
                    <i class="fas fa-play"></i>
                </span>
            </span>
            <span class="help-video-info">
                <span class="help-title-wrapper">
                    <span class="help-video-title">${escapeHtml(video.title)}</span>
                    <span class="help-video-icon" aria-hidden="true"><i class="fas fa-film"></i></span>
                </span>
                ${tags ? `<span class="help-video-tags">${tags}</span>` : ''}
            </span>
        </button>
    `;
}

function setupPagination(videos) {
    const pagination = document.getElementById('helpPagination');
    if (totalPages <= 1) {
        // A single page has nothing to page through.
        toggle(pagination, false);
        return;
    }
    toggle(pagination, true);

    document.getElementById('helpPrev').addEventListener('click', () => {
        if (currentPage > 1) {
            goToPage(videos, currentPage - 1);
        }
    });
    document.getElementById('helpNext').addEventListener('click', () => {
        if (currentPage < totalPages) {
            goToPage(videos, currentPage + 1);
        }
    });
    document.getElementById('helpPageNumbers').addEventListener('click', (event) => {
        const button = event.target.closest('[data-page]');
        if (button) {
            goToPage(videos, Number(button.dataset.page));
        }
    });
}

/** Paging keeps the grid in view: without this the reader is left below the new first row. */
function goToPage(videos, page) {
    renderPage(videos, page);
    document.getElementById('helpVideos').scrollIntoView({behavior: 'smooth', block: 'start'});
}

function renderPagination() {
    const numbers = document.getElementById('helpPageNumbers');
    if (!numbers) {
        return;
    }

    let markup = '';
    for (let page = 1; page <= totalPages; page++) {
        const active = page === currentPage ? ' active' : '';
        const current = page === currentPage ? ' aria-current="page"' : '';
        markup += `<button type="button" class="help-page-number${active}" data-page="${page}"${current}>${page}</button>`;
    }
    numbers.innerHTML = markup;

    document.getElementById('helpPrev').disabled = currentPage === 1;
    document.getElementById('helpNext').disabled = currentPage === totalPages;
}

function setupLightbox() {
    const lightbox = document.getElementById('helpLightbox');
    if (!lightbox) {
        return;
    }

    document.getElementById('helpLightboxClose').addEventListener('click', closeLightbox);
    lightbox.querySelectorAll('[data-help-close]').forEach((element) => {
        element.addEventListener('click', closeLightbox);
    });
    // Escape closes it, as it does for the in-app modals.
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !lightbox.hidden) {
            closeLightbox();
        }
    });
}

function openLightbox(card) {
    const lightbox = document.getElementById('helpLightbox');
    const videoId = card.dataset.videoId;
    const title = card.dataset.videoTitle;

    lastFocusedCard = card;
    document.getElementById('helpLightboxTitle').textContent = title;
    // Set on open rather than at render time, so seven players are not loaded behind the grid.
    //
    // playsinline is what keeps the video in this dialog on iOS. Without it Safari hands the
    // stream to its own fullscreen player, which leaves the lightbox controls behind and is why
    // pause and fullscreen appeared to do nothing on a phone.
    document.getElementById('helpLightboxFrame').src =
        `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}`
        + '?autoplay=1&playsinline=1&rel=0';

    toggle(lightbox, true);
    document.body.classList.add('help-lightbox-open');
    document.getElementById('helpLightboxClose').focus();
}

function closeLightbox() {
    const lightbox = document.getElementById('helpLightbox');
    // Pointing the frame away is what actually stops playback - hiding the dialog does not.
    // about:blank rather than an empty src, which some browsers resolve back to this page.
    document.getElementById('helpLightboxFrame').src = 'about:blank';
    toggle(lightbox, false);
    document.body.classList.remove('help-lightbox-open');

    if (lastFocusedCard) {
        lastFocusedCard.focus();
        lastFocusedCard = null;
    }
}

function toggle(element, visible) {
    if (element) {
        element.hidden = !visible;
    }
}

/** Titles and tags are authored data, but they still land in markup - so they are escaped. */
function escapeHtml(value) {
    return String(value === undefined || value === null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
